import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'
import axiosInstanceApiGateway from 'config/apiServiceApiGateway/index'
import type { MassUploadResponseType } from './massUpload'

const { Get, Post, Put } = axiosInstanceApiGateway
const { REACT_APP_BASE_URL_API } = process.env
const baseUrl = REACT_APP_BASE_URL_API

// ============================================================
// Types — traced bottom-up from usePrintLabelCloudKitchen.ts
// ============================================================

/** Raw API response item — fields as returned by the backend */
export interface KitchenOrderApiItemType {
  id: number
  status: string
  order_line_item_id: number
  order_product_name: string
  order_product_sku: string
  order_product_quantity: number
  product_modifier: string
  cloud_kitchen: boolean
  modifiers: Record<string, unknown>[]
  add_ons: Record<string, unknown>[]
  customer_note: string
}

/** The kitchen order as returned by GET /kitchen/orders/:id */
export interface KitchenOrderApiType {
  order_id: string | number
  status: string
  customer_name: string
  invoice: string
  external_order?: {
    driver_pin?: string | null
    external_second_id?: string
    provider?: string
  }
  items: KitchenOrderApiItemType[]
}

/** Inner response envelope — thunk unwraps to this */
export interface KitchenOrderResponseInnerType {
  order: KitchenOrderApiType
}

/** Outer response envelope — what Axios returns */
export interface KitchenOrderResponseType {
  data: KitchenOrderResponseInnerType
}

// --- Update Fulfill Quantity ---

export interface UpdateFulfillQuantityItemType {
  id: number
  fulfill_quantity: number
  note: string
}

export interface UpdateFulfillQuantityPayloadType {
  items: UpdateFulfillQuantityItemType[]
}

// --- Cloud Kitchen Schedule ---

export interface CloudKitchenSchedulePayloadType {
  productId: string | number
  locationId: string | number
}

// ============================================================
// API Functions
// ============================================================

export const getScheduleCloudKitchenProductHub = ({
  productId,
  locationId,
}: CloudKitchenSchedulePayloadType) =>
  axios.get(`${baseUrl}/api/cloud-kitchen/schedule/${productId}/${locationId}`)

export const postScheduleCloudKitchenProductHub = (payload: Record<string, unknown>) =>
  axios.post(`${baseUrl}/api/cloud-kitchen/schedule`, payload)

export const getCloudKitchenBulkUploadTemplate = (params?: Record<string, unknown>) =>
  axios.get<{ data: { url?: string; file_url: string } }>(
    `${baseUrl}/api/cloud-kitchen/schedule/template`,
    { params },
  )

export const postUploadCloudKitchenBulkUpload = (data: FormData, config: AxiosRequestConfig) =>
  axios.post<MassUploadResponseType>(
    `${baseUrl}/api/cloud-kitchen/schedule/bulk-upload`,
    data,
    config,
  )

export const getDataAndStatusAstroKitchenOrder = (orderId: string | number) =>
  Get<KitchenOrderResponseType>({
    url: `/fulfillment/internal/v1/kitchen/orders/${orderId}`,
  })

export const postDataAndCompleteAstroKitchen = (
  orderId: string | number,
  payload: Record<string, unknown>,
) => Post({ url: `/fulfillment/internal/v1/kitchen/orders/${orderId}/finish`, data: payload })

export const putDataUpdateItemOrderKitchen = (
  orderId: string | number,
  payload: UpdateFulfillQuantityPayloadType,
) => Put({ url: `/fulfillment/internal/v1/kitchen/orders/${orderId}/items`, data: payload })

export const postAuthKitchenStaff = (payload: Record<string, unknown>) => {
  axios.post(`${baseUrl}/api/admin/kitchen-auth`, payload)
}
